import type { UserDto } from '../user/user.schema';
import type {
  AcceptPoliciesRequest,
  AuthResponse,
  ForgotPasswordRequest,
  LoginRequest,
  RegisterRequest,
  ResendVerificationRequest,
  ResetPasswordRequest,
  VerifyEmailRequest,
  VerifyEmailResponse,
} from '@/queries/auth/auth.schema';
import {
  AcceptPoliciesRequestSchema,
  AuthResponseSchema,
  ForgotPasswordRequestSchema,
  LoginRequestSchema,
  RegisterRequestSchema,
  ResendVerificationRequestSchema,
  ResetPasswordRequestSchema,
  VerifyEmailRequestSchema,
  VerifyEmailResponseSchema,
} from '@/queries/auth/auth.schema';
import { ENDPOINTS } from '@/queries/endpoints';
import { httpClient } from '@/queries/httpClient';
import { validateWithZod } from '@/utils/zod.utils';
import { tokenManager } from '../token-manager';
import { UserDtoSchema } from '../user/user.schema';

export const authApi = {
  async register(data: RegisterRequest, headers?: Record<string, string>): Promise<UserDto> {
    const validatedData = validateWithZod(RegisterRequestSchema, data);

    const response = await httpClient.post<UserDto>(ENDPOINTS.auth.register, validatedData, {
      headers,
      skipAuth: true,
    });

    return validateWithZod(UserDtoSchema, response);
  },

  /**
   * Redirects the user to the Google registration endpoint.
   *
   * ⚠️ For local Google authentication (including cookies) to work, you must:
   * 1. Run the dev server with HTTPS and a custom domain.
   * 2. Follow the instructions in the README section:
   *    "Running the Dev Server with HTTPS and Custom Domain"
   *
   * This setup is required for Google auth cookies to be set correctly in local development.
   */
  async registerWithGoogle(queryParams?: string): Promise<void> {
    const baseUrl = `${process.env.NEXT_PUBLIC_API_URL}${ENDPOINTS.auth.registerWithGoogle}`;
    window.location.href = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
  },

  async registerWithFacebook(queryParams?: string): Promise<void> {
    const baseUrl = `${process.env.NEXT_PUBLIC_API_URL}${ENDPOINTS.auth.registerWithFacebook}`;
    window.location.href = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
  },

  async registerWithApple(queryParams?: string): Promise<void> {
    const baseUrl = `${process.env.NEXT_PUBLIC_API_URL}${ENDPOINTS.auth.registerWithApple}`;
    window.location.href = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
  },

  async login(data: LoginRequest, headers?: Record<string, string>): Promise<AuthResponse> {
    const validatedData = validateWithZod(LoginRequestSchema, data);

    const response = await httpClient.post<AuthResponse>(ENDPOINTS.auth.login, validatedData, {
      headers,
      skipAuth: true,
    });

    return validateWithZod(AuthResponseSchema, response);
  },

  async storeSeonInSession(sessionId: string): Promise<{ sessionRef?: string }> {
    const response = await httpClient.post<{ sessionRef?: string }>(ENDPOINTS.auth.storeSeonSession, {
      antiFraudSessionId: sessionId,
    }, { skipAuth: true });
    return response;
  },

  async intercomToken(): Promise<{ token: string }> {
    const response = await httpClient.get<{ token: string }>(ENDPOINTS.auth.intercom);

    return response;
  },

  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = tokenManager.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await httpClient.post<AuthResponse>(
      ENDPOINTS.auth.refresh,
      {},
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
        // Important: prevent recursion (refresh endpoint must not trigger access-token refresh flow)
        skipAuth: true,
        _retry: true,
      },
    );

    return validateWithZod(AuthResponseSchema, response);
  },

  async verifyEmail(data: VerifyEmailRequest): Promise<VerifyEmailResponse | undefined> {
    const validatedData = validateWithZod(VerifyEmailRequestSchema, data);

    const response = await httpClient.get<unknown>(ENDPOINTS.auth.verifyEmail, {
      params: validatedData,
      skipAuth: true,
    });

    const parsed = VerifyEmailResponseSchema.safeParse(response);
    return parsed.success ? parsed.data : undefined;
  },

  async resendVerification(data: ResendVerificationRequest): Promise<void> {
    const validatedData = validateWithZod(ResendVerificationRequestSchema, data);

    await httpClient.post<void>(ENDPOINTS.auth.resendVerification, validatedData, { skipAuth: true });
  },

  async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    const validatedData = validateWithZod(ForgotPasswordRequestSchema, data);

    await httpClient.post<void>(ENDPOINTS.auth.forgotPassword, validatedData, { skipAuth: true });
  },

  async resetPassword(data: ResetPasswordRequest & { token: string }): Promise<void> {
    const { token, ...passwords } = data;
    const validatedData = ResetPasswordRequestSchema.parse(passwords);

    await httpClient.post<void>(ENDPOINTS.auth.resetPassword, validatedData, {
      params: { token },
      skipAuth: true,
    });
  },

  async acceptPolicies(data: AcceptPoliciesRequest, headers?: Record<string, string>): Promise<AuthResponse> {
    const validatedData = validateWithZod(AcceptPoliciesRequestSchema, data);

    const response = await httpClient.post<AuthResponse>(ENDPOINTS.auth.acceptPolicies, validatedData, {
      headers,
    });

    return validateWithZod(AuthResponseSchema, response);
  },

  async logout(): Promise<void> {
    // Avoid triggering refresh flow during logout (especially when refresh token is invalid)
    await httpClient.post<void>(ENDPOINTS.auth.logout, undefined, { skipAuth: false, _retry: true });
  },

  async logoutAll(): Promise<void> {
    // Avoid triggering refresh flow during logout (especially when refresh token is invalid)
    await httpClient.post<void>(ENDPOINTS.auth.logoutAll, undefined, { skipAuth: false, _retry: true });
  },
};
